/**
 * Facturation : factures, encaissements, avoirs, relances et ordonnances de paiement.
 *
 * Ensembles fermés partagés entre la base (pgEnum), le serveur (zod) et le front (libellés i18n).
 * Règle : valeurs ASCII en snake_case, jamais de libellé humain ici. Ce fichier est réexporté par
 * `../enums.js`, qui reste le seul point d'entrée : personne n'importe un domaine directement.
 */

import type { MemberRole } from './identity.js';

/**
 * Ces ensembles doublent volontairement ceux de `@osteopro/invoicing`. Ce paquet-ci est la source
 * de la base (`pgEnum`) et du contrat réseau, et il ne peut pas dépendre de `@osteopro/invoicing`
 * qui dépend déjà de lui. L'égalité des deux listes n'est donc pas confiée à la relecture :
 * `apps/api/src/modules/billing/billing.enums.ts` la vérifie à la compilation, comme le module
 * consultation le fait déjà pour les codes de questionnaires de `@osteopro/clinical`.
 */

/** Régime de TVA du cabinet. La décision « exonération ou 17 % » appartient au cabinet (docs/BACKLOG.md). */
export const vatModes = ['exempt', 'standard'] as const;
export type VatMode = (typeof vatModes)[number];

/** Séries de numérotation comptable : `FAC-LU-2026-0001`, `AVOIR-LU-2026-0001`. */
export const invoiceSeries = ['FAC', 'AVOIR'] as const;
export type InvoiceSeries = (typeof invoiceSeries)[number];

/** `cancelled` ne s'atteint que sans encaissement ; sinon on passe par un avoir. */
export const invoiceStatuses = ['draft', 'issued', 'partially_paid', 'paid', 'cancelled'] as const;
export type InvoiceStatus = (typeof invoiceStatuses)[number];

/**
 * Moyens de paiement luxembourgeois. Pas de chèque : il n'existait qu'en France dans la V1.
 *
 * `payconiq` et `wero` restent dans l'ensemble pour les lignes déjà écrites. On ne les coche plus
 * dans Paramètres → Facturation : la liste manuelle est `manualPaymentMethods`, et Wero suit
 * l'interrupteur de Paramètres → Wero (`payment-method-catalog.ts`).
 */
export const paymentMethods = [
  'cash',
  'card',
  'transfer',
  'wero',
  'payconiq',
  'sumup',
  'other',
] as const;
export type PaymentMethod = (typeof paymentMethods)[number];

/**
 * Prestataire d'encaissement externe rapprochant le paiement. `payconiq` n'est plus écrit par
 * aucun code : il reste parce qu'un registre comptable ne se réécrit pas, et que des lignes
 * historiques peuvent le porter.
 */
export const paymentProviders = ['payconiq', 'buckaroo'] as const;
export type PaymentProvider = (typeof paymentProviders)[number];

/**
 * État d'une ligne du registre des paiements. `recorded` : encaissement constaté par le cabinet.
 * `pending` / `failed` : encaissement confié à un prestataire, en attente ou refusé.
 */
export const paymentStatuses = ['recorded', 'pending', 'failed'] as const;
export type PaymentStatus = (typeof paymentStatuses)[number];

/** `applied` est posé par le calcul d'imputation quand l'avoir est entièrement consommé. */
export const creditNoteStatuses = ['draft', 'issued', 'applied', 'cancelled'] as const;
export type CreditNoteStatus = (typeof creditNoteStatuses)[number];

export const creditNoteReasons = ['error', 'commercial_gesture', 'cancellation', 'other'] as const;
export type CreditNoteReason = (typeof creditNoteReasons)[number];

/** Une relance est générée en attente de validation ; le praticien l'envoie ou l'annule. */
export const dunningNoticeStatuses = ['pending_validation', 'sent', 'cancelled'] as const;
export type DunningNoticeStatus = (typeof dunningNoticeStatuses)[number];

/** Canal d'envoi effectif d'une relance validée. */
export const dunningChannels = ['email', 'download'] as const;
export type DunningChannel = (typeof dunningChannels)[number];

/**
 * Intérêts de retard dus sur une facture. `open` : réclamés par la dernière relance envoyée, pas
 * encore entièrement reçus. `paid` : reçus en entier, ou arrêtés à ce qui avait été reçu quand une
 * relance plus récente les a remplacés. `waived` : abandonnés par le cabinet.
 */
export const lateInterestStatuses = ['open', 'paid', 'waived'] as const;
export type LateInterestStatus = (typeof lateInterestStatuses)[number];

/**
 * Intérêts de retard d'une étape de relance, jamais composés, toujours calculés sur le reste dû :
 * `none` aucun, `percentage` pourcentage forfaitaire, `fixed` montant forfaitaire, `annual_rate`
 * taux annuel au prorata des jours écoulés depuis l'échéance — celui du taux légal que le cabinet
 * saisit lui-même, parce qu'il change chaque année par règlement grand-ducal.
 */
export const interestTypes = ['none', 'percentage', 'fixed', 'annual_rate'] as const;
export type InterestType = (typeof interestTypes)[number];

export const dunningLevels = [1, 2, 3] as const;
export type DunningLevel = (typeof dunningLevels)[number];

/**
 * Cycle de vie d'une requête en ordonnance de paiement. La V1 en déclarait dix, dont quatre que
 * rien ne posait ni n'affichait ; on garde les six que l'écran sait produire et lire.
 */
export const paymentOrderStatuses = [
  'draft',
  'filed',
  'granted',
  'paid',
  'expired',
  'cancelled',
] as const;
export type PaymentOrderStatus = (typeof paymentOrderStatuses)[number];

/** Les trois Justices de Paix luxembourgeoises. */
export const justicesDePaix = ['Luxembourg', 'Esch-sur-Alzette', 'Diekirch'] as const;
export type JusticeDePaix = (typeof justicesDePaix)[number];

/** Modèles de rétrocession : seul le sens du flux change entre eux. */
export const practitionerTypes = ['collaborator', 'substitute', 'employee'] as const;
export type PractitionerType = (typeof practitionerTypes)[number];

/**
 * Rôles qui ouvrent une rétrocession, et modèle appliqué. Le propriétaire encaisse pour lui-même
 * et le secrétariat ne facture pas : ni l'un ni l'autre n'a de taux.
 *
 * **Le salarié** est « spécial » (Nicolas, 30 septembre 2026) : payé par le titulaire, il lui
 * laisse **100 % de ses honoraires** par défaut. Son modèle `employee` compte ce que le titulaire
 * lui *reverse* (`retrocessionDirections`) : 100 % au titulaire s'écrit donc un taux reversé de
 * 0 %, et c'est ce que lit le rapport pour un salarié sans période saisie. Le sens du modèle n'a
 * pas été retourné : les périodes déjà saisies gardent leur signification.
 */
export const retrocessionModelByRole: Readonly<Partial<Record<MemberRole, PractitionerType>>> = {
  collaborateur: 'collaborator',
  remplacant: 'substitute',
  salarie: 'employee',
};

/** Taux reversé d'un salarié sans période saisie : rien, tout revient au titulaire. */
export const EMPLOYEE_DEFAULT_RATE_BPS = 0;
